const ENTITY_TYPE_PATTERN = /^[a-z][a-z0-9_]{1,79}$/;
const ACTION_PATTERN = /^[a-z][a-z0-9_.]{1,99}$/;

const trimmed = value => String(value || '').trim();

const isRealIsoDate = value => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

export const AUDIT_MODULE_OPTIONS = [
  { value: '', label: 'सर्व विभाग (All Modules)', icon: '📋', badgeClass: 'all' },
  { value: 'tax', label: 'कर व आकारणी (Tax & Demands)', icon: '🏛️', badgeClass: 'tax' },
  { value: 'finance', label: 'आर्थिक व भरणा (Finance & Payments)', icon: '💰', badgeClass: 'finance' },
  { value: 'certificates', label: 'प्रमाणपत्रे (Certificates)', icon: '📜', badgeClass: 'certificates' },
  { value: 'registry', label: 'नोंदणी व मालमत्ता (Registry & Assets)', icon: '🏡', badgeClass: 'registry' },
  { value: 'security', label: 'सुरक्षा व वापरकर्ते (Security & Access)', icon: '🔐', badgeClass: 'security' },
];

export const MODULE_ENTITY_PRESETS = {
  tax: [
    { value: '', label: 'सर्व कर नोंदी' },
    { value: 'demand_adjustment', label: 'कर ॲडजस्टमेंट (Demand Adjustment)' },
    { value: 'tax_demand', label: 'कर मागणी (Tax Demand)' },
    { value: 'assessment_run', label: 'कर आकारणी रन (Assessment Run)' },
    { value: 'tax_account', label: 'कर खाते (Tax Account)' },
    { value: 'tax_rule', label: 'कर नियम (Tax Rule)' },
    { value: 'tax_appeal', label: 'कर अपील (Tax Appeal)' },
    { value: 'tax_exemption', label: 'कर सवलत (Tax Exemption)' },
  ],
  finance: [
    { value: '', label: 'सर्व आर्थिक नोंदी' },
    { value: 'tax_payment', label: 'कर भरणा (Tax Payment)' },
    { value: 'tax_receipt', label: 'कर पावती (Tax Receipt)' },
    { value: 'manual_collection_attempt', label: 'मॅन्युअल वसुली (Manual Collection)' },
    { value: 'tax_payment_intent', label: 'ऑनलाइन भरणा आदेश (Payment Intent)' },
    { value: 'tax_receipt_action', label: 'पावती कृती / परतावा (Receipt Action)' },
  ],
  certificates: [
    { value: '', label: 'सर्व प्रमाणपत्र नोंदी' },
    { value: 'issued_certificate', label: 'वितरित प्रमाणपत्र (Issued Certificate)' },
    { value: 'certificate_application', label: 'प्रमाणपत्र अर्ज (Application)' },
    { value: 'certificate_template', label: 'प्रमाणपत्र मसुदा (Template)' },
  ],
  registry: [
    { value: '', label: 'सर्व मालमत्ता व नोंदणी' },
    { value: 'property', label: 'मालमत्ता (Property)' },
    { value: 'business', label: 'व्यवसाय (Business)' },
    { value: 'household', label: 'कुटुंब (Household)' },
    { value: 'party', label: 'नागरिक / व्यक्ती (Citizen / Party)' },
  ],
  security: [
    { value: '', label: 'सर्व सुरक्षा नोंदी' },
    { value: 'user', label: 'वापरकर्ता (User)' },
    { value: 'role', label: 'भूमिका (Role)' },
    { value: 'membership', label: 'सदस्यत्व (Membership)' },
  ],
};

export const emptyAuditFilters = () => ({
  module: '',
  q: '',
  entity_type: '',
  entity_id: '',
  action: '',
  actor_user_id: '',
  from: '',
  to: '',
});

export const validateAuditFilters = values => {
  const filters = { ...emptyAuditFilters(), ...(values || {}) };
  const moduleVal = trimmed(filters.module);
  const q = trimmed(filters.q);
  const entityType = trimmed(filters.entity_type);
  const entityId = trimmed(filters.entity_id);
  const action = trimmed(filters.action);
  const actorId = trimmed(filters.actor_user_id);
  const from = trimmed(filters.from);
  const to = trimmed(filters.to);

  if (moduleVal && !AUDIT_MODULE_OPTIONS.some(option => option.value === moduleVal)) {
    return 'Module filter is invalid.';
  }
  if (q.length > 100) return 'Search keyword cannot exceed 100 characters.';
  if (entityType) {
    const list = entityType.split(',').map(s => s.trim()).filter(Boolean);
    for (const item of list) {
      if (!ENTITY_TYPE_PATTERN.test(item)) {
        return 'Entity type must use 2-80 lowercase letters, numbers, or underscores and start with a letter.';
      }
    }
  }
  if (entityId.length > 100) return 'Entity ID cannot exceed 100 characters.';
  if (action && !ACTION_PATTERN.test(action)) {
    return 'Action must use 2-100 lowercase letters, numbers, dots, or underscores and start with a letter.';
  }
  if (actorId && (!/^\d+$/.test(actorId) || Number(actorId) < 1 || !Number.isSafeInteger(Number(actorId)))) {
    return 'Actor user ID must be a positive whole number.';
  }
  if (from && !isRealIsoDate(from)) return 'From date is invalid.';
  if (to && !isRealIsoDate(to)) return 'To date is invalid.';
  if (from && to && from > to) return 'To date cannot be before From date.';
  return '';
};

export const buildAuditParams = (values, pagination = {}) => {
  const params = {};
  Object.entries({ ...emptyAuditFilters(), ...(values || {}) }).forEach(([key, value]) => {
    const normalized = trimmed(value);
    if (normalized) params[key] = key === 'actor_user_id' ? Number(normalized) : normalized;
  });
  if (pagination.page != null) params.page = Math.max(1, Number(pagination.page) || 1);
  if (pagination.limit != null) params.limit = Math.min(100, Math.max(1, Number(pagination.limit) || 25));
  return params;
};

export const humanizeAuditToken = value => trimmed(value)
  .replace(/[._]+/g, ' ')
  .replace(/\b\w/g, letter => letter.toUpperCase()) || 'Unknown';

export const formatAuditDateTime = value => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'medium',
  }).format(date);
};

export const prettyAuditJson = value => value == null
  ? 'No snapshot recorded.'
  : JSON.stringify(value, null, 2);

export const auditErrorMessage = error => (
  error?.response?.data?.error
  || error?.response?.data?.message
  || error?.message
  || 'Audit events could not be loaded.'
);

export const auditExportFilename = date => `audit-events-${date.toISOString().slice(0, 10)}.csv`;

export const extractFinancialDetails = event => {
  if (!event) return null;
  const after = event.after || {};
  const before = event.before || {};

  const hasAmount = after.amount != null || before.amount != null;
  const hasSigned = after.signed_amount != null;
  const hasTotal = after.total_amount != null || before.total_amount != null;

  if (!hasAmount && !hasSigned && !hasTotal && !event.is_financial) return null;

  const currencyFmt = val => val == null ? '—' : `₹${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const amountVal = after.signed_amount != null
    ? after.signed_amount
    : after.amount != null
    ? after.amount
    : null;

  const totalBefore = before.total_amount != null ? Number(before.total_amount) : null;
  const totalAfter = after.total_amount != null ? Number(after.total_amount) : null;
  const totalDiff = (totalBefore != null && totalAfter != null) ? totalAfter - totalBefore : null;

  return {
    amountFormatted: amountVal != null ? currencyFmt(amountVal) : null,
    totalBeforeFormatted: totalBefore != null ? currencyFmt(totalBefore) : null,
    totalAfterFormatted: totalAfter != null ? currencyFmt(totalAfter) : null,
    differenceFormatted: totalDiff != null ? `${totalDiff > 0 ? '+' : ''}${currencyFmt(totalDiff)}` : null,
    adjustmentType: after.adjustment_type || before.adjustment_type || null,
  };
};

export const groupEvents = (events, groupBy = 'none') => {
  if (!Array.isArray(events) || events.length === 0) return [];
  if (groupBy === 'none') {
    return [{ key: 'all', label: 'सर्व नोंदी', events }];
  }

  if (groupBy === 'module') {
    const groups = {};
    AUDIT_MODULE_OPTIONS.forEach(opt => {
      if (opt.value) groups[opt.value] = { key: opt.value, label: opt.label, icon: opt.icon, events: [] };
    });
    groups.general = { key: 'general', label: 'इतर प्रणाली नोंदी (General)', icon: '⚙️', events: [] };

    events.forEach(event => {
      const mod = event.module || 'general';
      if (!groups[mod]) groups[mod] = { key: mod, label: humanizeAuditToken(mod), icon: '📌', events: [] };
      groups[mod].events.push(event);
    });

    return Object.values(groups).filter(g => g.events.length > 0);
  }

  if (groupBy === 'date') {
    const groups = {};
    events.forEach(event => {
      const dateKey = event.occurred_at ? new Date(event.occurred_at).toISOString().slice(0, 10) : 'unknown';
      if (!groups[dateKey]) {
        const label = dateKey === 'unknown' ? 'तारीख नोंद नाही' : new Intl.DateTimeFormat('en-IN', { dateStyle: 'full' }).format(new Date(dateKey));
        groups[dateKey] = { key: dateKey, label, icon: '📅', events: [] };
      }
      groups[dateKey].events.push(event);
    });
    return Object.values(groups);
  }

  if (groupBy === 'actor') {
    const groups = {};
    events.forEach(event => {
      const actorKey = event.actor?.id ? `user-${event.actor.id}` : 'system';
      if (!groups[actorKey]) {
        const label = event.actor?.full_name ? `${event.actor.full_name} (${event.actor.email || `#${event.actor.id}`})` : 'प्रणाली (System / Automated)';
        groups[actorKey] = { key: actorKey, label, icon: '👤', events: [] };
      }
      groups[actorKey].events.push(event);
    });
    return Object.values(groups);
  }

  return [{ key: 'all', label: 'सर्व नोंदी', events }];
};
